'use client';
import React, { useState, useEffect, useCallback } from 'react';
import { createClient } from '@/lib/supabase/client';
import { AlertTriangle, Search, RefreshCw, Trash2, Clock, Phone, Globe, FileWarning } from 'lucide-react';

interface DeletedLeadLog {
  id: string;
  deleted_at: string;
  phone: string;
  lead_name: string | null;
  source: string | null;
  failure_reason: string;
  message_sid: string | null;
}

function formatDateTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
}

function ReasonBadge({ reason }: { reason: string }) {
  const lower = reason.toLowerCase();
  let color = 'bg-red-100 text-red-700';
  if (lower.includes('undelivered')) color = 'bg-orange-100 text-orange-700';
  else if (lower.includes('failed')) color = 'bg-red-100 text-red-700';
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${color}`}>
      <FileWarning size={11} />
      {reason}
    </span>
  );
}

export default function DeletedLeadsLogClient() {
  const [logs, setLogs] = useState<DeletedLeadLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const perPage = 15;

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const supabase = createClient();
      const { data, error: fetchErr } = await supabase
        .from('deleted_leads_log')
        .select('*')
        .order('deleted_at', { ascending: false });

      if (fetchErr) throw fetchErr;
      setLogs(data ?? []);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to load deleted leads log';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const filtered = logs.filter((l) => {
    const q = search.toLowerCase();
    return (
      l.phone?.toLowerCase().includes(q) ||
      (l.lead_name ?? '').toLowerCase().includes(q) ||
      (l.source ?? '').toLowerCase().includes(q) ||
      l.failure_reason?.toLowerCase().includes(q)
    );
  });

  const totalPages = Math.ceil(filtered.length / perPage);
  const paginated = filtered.slice((page - 1) * perPage, page * perPage);

  return (
    <div className="flex flex-col h-full bg-background min-h-screen">
      {/* Header */}
      <div className="px-6 pt-6 pb-4 border-b border-border bg-white">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-red-50 flex items-center justify-center">
              <Trash2 size={18} className="text-red-500" />
            </div>
            <div>
              <h1 className="text-lg font-semibold text-foreground leading-tight">Auto-Deleted Leads Log</h1>
              <p className="text-xs text-muted-foreground mt-0.5">
                Leads removed due to failed WhatsApp delivery — for compliance &amp; pattern detection
              </p>
            </div>
          </div>
          <button
            onClick={fetchLogs}
            disabled={loading}
            className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-muted-foreground border border-border rounded-lg hover:bg-muted/50 transition-colors disabled:opacity-50"
          >
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
            Refresh
          </button>
        </div>

        {/* Stats row */}
        <div className="flex items-center gap-6 mt-4">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <AlertTriangle size={14} className="text-red-400" />
            <span>
              <span className="font-semibold text-foreground">{logs.length}</span> total deleted
            </span>
          </div>
          {search && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <span>
                <span className="font-semibold text-foreground">{filtered.length}</span> matching
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Search */}
      <div className="px-6 py-3 bg-white border-b border-border">
        <div className="relative max-w-sm">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search by phone, name, source, reason..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            className="w-full pl-9 pr-3 py-2 text-sm border border-border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
          />
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto px-6 py-4">
        {error && (
          <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm mb-4">
            <AlertTriangle size={16} />
            <span>{error}</span>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-20 text-muted-foreground text-sm gap-3">
            <RefreshCw size={16} className="animate-spin" />
            Loading deleted leads log...
          </div>
        ) : paginated.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <div className="w-14 h-14 rounded-full bg-muted flex items-center justify-center mb-4">
              <Trash2 size={24} className="text-muted-foreground" />
            </div>
            <p className="text-sm font-medium text-foreground">
              {search ? 'No results found' : 'No deleted leads yet'}
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              {search
                ? 'Try a different search term'
                : 'Leads auto-deleted due to failed WhatsApp delivery will appear here'}
            </p>
          </div>
        ) : (
          <div className="bg-white border border-border rounded-xl overflow-hidden shadow-sm">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-muted/40 border-b border-border">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                    <span className="flex items-center gap-1.5"><Clock size={12} /> Deleted At</span>
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                    <span className="flex items-center gap-1.5"><Phone size={12} /> Phone</span>
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                    Lead Name
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                    <span className="flex items-center gap-1.5"><Globe size={12} /> Source</span>
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                    <span className="flex items-center gap-1.5"><FileWarning size={12} /> Failure Reason</span>
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                    Message SID
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {paginated.map((log) => (
                  <tr key={log.id} className="hover:bg-muted/20 transition-colors">
                    <td className="px-4 py-3 text-xs text-muted-foreground whitespace-nowrap">
                      {formatDateTime(log.deleted_at)}
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-foreground whitespace-nowrap">
                      {log.phone}
                    </td>
                    <td className="px-4 py-3 text-sm text-foreground">
                      {log.lead_name ?? <span className="text-muted-foreground italic">Unknown</span>}
                    </td>
                    <td className="px-4 py-3 text-sm text-foreground">
                      {log.source ?? <span className="text-muted-foreground italic">—</span>}
                    </td>
                    <td className="px-4 py-3">
                      <ReasonBadge reason={log.failure_reason} />
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-muted-foreground truncate max-w-[160px]" title={log.message_sid ?? ''}>
                      {log.message_sid ?? <span className="italic">—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-border bg-muted/20">
                <span className="text-xs text-muted-foreground">
                  Showing {(page - 1) * perPage + 1}–{Math.min(page * perPage, filtered.length)} of {filtered.length}
                </span>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="px-2.5 py-1 text-xs border border-border rounded hover:bg-muted disabled:opacity-40 transition-colors"
                  >
                    Prev
                  </button>
                  {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                    const pg = i + 1;
                    return (
                      <button
                        key={pg}
                        onClick={() => setPage(pg)}
                        className={`px-2.5 py-1 text-xs border rounded transition-colors ${
                          page === pg
                            ? 'bg-primary text-white border-primary' :'border-border hover:bg-muted'
                        }`}
                      >
                        {pg}
                      </button>
                    );
                  })}
                  <button
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className="px-2.5 py-1 text-xs border border-border rounded hover:bg-muted disabled:opacity-40 transition-colors"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
